import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Alert, Button, Grid, Input, styles, Surface} from "tmwx-design-web/src";
import {usePermissions} from "tmwx-design-common";
import {useSearchParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {checkFieldError, isEmpty} from "gui-common/appHelper";
import {getFieldErrorText} from "../../utils/helper";
import {RootState} from "gui-common/redux/store";
import {ResponseBase} from "gui-common/redux/data";
import {
    AddMetricsRequest,
    initialAddMetricsRequest,
    isAddMetricsRequestValid,
    MetricJsonGenResponse
} from "gui-common/redux/data/metrics";
import {
    addNewMetrics,
    clearMetricJsonGenResponse,
    clearMetricsError,
    clearMetricsResponse,
    getMetricJsonGen
} from "gui-common/redux/action/efe/metricsAction";

interface HandleMetricsSendProps {
    onClose?: () => void
}

const MetricsSend: React.FC<HandleMetricsSendProps> = ({
                                                           onClose = () => {
                                                           }
                                                       }) => {
    const {t} = useTranslation()
    const dispatch: any = useDispatch();
    const {hasRights} = usePermissions()

    const [searchParams] = useSearchParams();
    const [validationMessage, setValidationMessage] = useState<string>("");

    const serverError = useSelector<RootState, ResponseBase | null>(state => state.metrics.error);
    const response = useSelector<RootState, ResponseBase | null>(state => state.metrics.response);
    const metricJsonGenResponse = useSelector<RootState, MetricJsonGenResponse | null>(state => state.metrics.metricJsonGenResponse);
    const [request, setRequest] = useState<AddMetricsRequest>(initialAddMetricsRequest)
    const podId = searchParams.get("podId") as string;

    const handleClear = () => {
        setRequest(initialAddMetricsRequest)
        dispatch(clearMetricsResponse())
        dispatch(clearMetricsError())
        dispatch(clearMetricJsonGenResponse())
        setValidationMessage('')
        onClose()
    }

    const handleChangeName = (text: string) => {
        if (serverError) {
            dispatch(clearMetricsError())
        }

        setRequest({...request, name: text})
    }

    const handleChangeMetricsJson = (text: string) => {
        if (serverError) {
            dispatch(clearMetricsError())
        }

        setRequest({...request, metricsJson: text})
    }

    const handleGenerateMetricJson = () => {
        if (!isEmpty(podId) && hasRights(["efe_metrics_create"])) {
            dispatch(clearMetricJsonGenResponse())
            dispatch(getMetricJsonGen(podId))
        }
    }

    useEffect(() => {
        const systemId = searchParams.get("systemId") as string;
        if (!isEmpty(systemId) && hasRights(["efe_metrics_create"])) {
            setRequest(prev => ({...prev, systemId: systemId}))
        }
    }, [searchParams, hasRights])

    useEffect(() => {
        if (metricJsonGenResponse) {
            if (serverError) {
                dispatch(clearMetricsError())
            }

            setRequest(prev => ({
                ...prev,
                metricsJson: JSON.stringify(metricJsonGenResponse, null, 2)
            }))
            dispatch(clearMetricJsonGenResponse())
        }
    }, [dispatch, metricJsonGenResponse, serverError])

    const handleAddMetricClick = () => {
        setValidationMessage('')
        if (isAddMetricsRequestValid(request as AddMetricsRequest)) {
            dispatch(addNewMetrics(request as AddMetricsRequest));
        } else {
            setValidationMessage(t('mandatoryFields'));
        }
    }

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClear()
            }
        }
    }, [response])

    return (
        <Surface elevation={0}>
            <Grid.Container centered maxWidth="xl">
                <Grid.Col s={12} m={12} lg={12} xl={12}>
                    {!isEmpty(validationMessage) && (
                        <Alert style={{marginBottom: 8}} text={validationMessage} status="danger"/>
                    )}

                    {serverError && (
                        <Alert style={{marginBottom: 8}} status="danger" text={t('errorOccurred')}/>
                    )}
                </Grid.Col>

                <Grid.Col s={12} m={12} lg={12} xl={12}>
                    <Input
                        fullWidth
                        error={checkFieldError('name', serverError)}
                        icon={"badge_outlined"}
                        placeholder={t('name')}
                        value={request.name}
                        onChangeValue={(text) => handleChangeName(text)}
                        helperText={getFieldErrorText('name', serverError, t)}
                    />
                </Grid.Col>

                <Grid.Col s={12} m={12} lg={12} xl={12}>
                    <Input
                        fullWidth
                        error={checkFieldError('metricsJson', serverError)}
                        icon={"description_outlined"}
                        placeholder={t('efe.metricsText')}
                        value={request.metricsJson}
                        multiline
                        numberOfLines={24}
                        onChangeValue={(text) => handleChangeMetricsJson(text)}
                        helperText={getFieldErrorText('metricsJson', serverError, t)}
                    />
                </Grid.Col>

                <Grid.Col s={12} m={12} lg={12} xl={12}>
                    <div style={{...styles.marginTop8, ...styles.row, ...{justifyContent: 'space-between', width: '100%'}}}>
                        <Button status="neutral" variant="outlined" onPress={handleGenerateMetricJson}>
                            {t('generate')}
                        </Button>

                        <div style={styles.row}>
                            <Button status="danger" variant="outlined" onPress={handleClear}>
                                {t('cancel')}
                            </Button>
                            <Button style={styles.marginLeft16} onPress={handleAddMetricClick}>
                                {t('send')}
                            </Button>
                        </div>
                    </div>
                </Grid.Col>
            </Grid.Container>
        </Surface>
    )
}
export default MetricsSend;
